import { Chip } from '@mui/material';
import { VyTooltip } from '../../../../../../components/new-design/VyTooltip';

import {
  getDiscountConfiguration,
  isCommercialTermsProduct,
  isCommercialTermsProductVariant,
  isCommercialTermsWithoutScheduledInstallments,
  isCommissionProduct,
  isCreditProduct,
  isFixedDeductionProductOnly,
  isOneTimeProduct,
  isPercentageDeductionProductOnly,
  isRefundProduct,
  isSubscriptionProduct,
  isSubscriptionProductVariant,
} from '@vy/services';
import type { FixedProductInput } from '@vy/types';
import { INTEGRATION_PROVIDERS, PAYMENT_TERMS } from '@vy/types';
import {
  isMultiple,
  isNotEmptyArray,
  isNotNullOrUndefined,
  isNullOrUndefined,
  raise,
  withDefault,
} from '@vy/utils';
import React, { useEffect } from 'react';
import type { FieldPath, UseFormGetValues } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import { useDialog } from '../../../../../../hooks/use-dialog';
import type { NewPlanForm } from '../../CreatePlanForm/create-plan-form-types/new-plan-form';
import type { PlanInputForm } from '../../CreatePlanForm/create-plan-form-types/plan-input-form';
import { FixedFeesModifyPanel } from '../../FixedFeesModifyPanel';
import { ModifyButton } from '../../ModifyButton';
import type { FieldPathMetadata } from '../fixed-charges-edit-table-types/field-path-metadata';

type FixedChargeAddonsCellProps = {
  fixedProduct: FixedProductInput;
  isInvoice?: boolean;
  isParentAgreement?: boolean;
};

export const FixedChargeAddonsCell: React.FC<FixedChargeAddonsCellProps> = ({
  fixedProduct,
  isInvoice = false,
  isParentAgreement = false,
}) => {
  const { isDialogOpen: isDrawerOpen, handleOpen, handleClose } = useDialog();
  const { getValues, setValue } = useFormContext<PlanInputForm, undefined, NewPlanForm>();
  const products = withDefault(getValues('fixedProducts'), []);
  const productIndex = products.findIndex((product) => product.id === fixedProduct.id);

  useEffect(() => {
    if (!isDrawerOpen) {
      const externalIntegration = getValues(`fixedProducts.${productIndex}.externalIntegration`);

      if (isNotNullOrUndefined(externalIntegration) && externalIntegration.id === '') {
        setValue(`fixedProducts.${productIndex}.externalIntegration`, undefined, { shouldDirty: true });
      }
    }
  }, [isDrawerOpen, productIndex, getValues, setValue]);

  const drawer = (
    <FixedFeesModifyPanel
      open={isDrawerOpen}
      onClose={handleClose}
      fixedProduct={fixedProduct}
      isInvoice={isInvoice}
      isParentAgreement={isParentAgreement}
    />
  );

  const addedFieldsLabels = getAdjustedAddedFieldsLabels(fixedProduct, productIndex, getValues);

  if (isNotEmptyArray(addedFieldsLabels)) {
    return (
      <>
        <div className="flex gap-x-1">
          {addedFieldsLabels.map(({ label, tooltip }) =>
            isNotNullOrUndefined(tooltip) ? (
              <VyTooltip key={label} tooltipContent={tooltip.title} placement="top" arrow>
                <Chip size="small" label={label} onClick={handleOpen} />
              </VyTooltip>
            ) : (
              <Chip key={label} size="small" label={label} onClick={handleOpen} />
            ),
          )}
        </div>

        {drawer}
      </>
    );
  }

  return (
    <>
      <ModifyButton handleOpen={handleOpen} isDialogOpen={isDrawerOpen} />

      {drawer}
    </>
  );
};

function getAdjustedAddedFieldsLabels(
  fixedProduct: FixedProductInput,
  productIndex: number,
  getValues: UseFormGetValues<PlanInputForm>,
) {
  const addedFieldsLabels = getAddedFieldsPathMetadata(fixedProduct, productIndex, getValues);

  if (addedFieldsLabels.length > 2) {
    return [
      ...addedFieldsLabels.slice(0, 2),
      {
        path: '' as FieldPath<FixedProductInput>,
        label: '+1',
      },
    ] satisfies FieldPathMetadata<FixedProductInput>[];
  }

  return addedFieldsLabels;
}

function getAddedFieldsPathMetadata(
  fixedProduct: FixedProductInput,
  productIndex: number,
  getValues: UseFormGetValues<PlanInputForm>,
) {
  const customFieldsPathsWithLabels = getFieldsPathsWithLabels(fixedProduct, productIndex, getValues);
  const erpIdPathWithLabel = getErpIdPathWithLabel(productIndex, getValues);
  const fieldsPathsWithLabels = [...customFieldsPathsWithLabels, ...erpIdPathWithLabel];

  return fieldsPathsWithLabels.filter(({ path }) => {
    const value = getValues(`fixedProducts.${productIndex}.${path}`);

    return isNotNullOrUndefined(value);
  });
}

function getErpIdPathWithLabel(
  productIndex: number,
  getValues: UseFormGetValues<PlanInputForm>,
): FieldPathMetadata<FixedProductInput>[] {
  const integrationEntities = getValues(`fixedProducts.${productIndex}.integrationEntities`);

  if (isNotNullOrUndefined(integrationEntities) && isNotEmptyArray(integrationEntities)) {
    const netsuiteEntity = integrationEntities.find(
      (entity) => entity.integrationProvider === INTEGRATION_PROVIDERS.netSuite,
    );

    if (isNotNullOrUndefined(netsuiteEntity)) {
      return [
        {
          path: 'integrationEntities',
          label: 'ERP ID',
          tooltip: {
            title: netsuiteEntity.integrationExternalId,
          },
        },
      ];
    }
  }

  return [];
}

function getFieldsPathsWithLabels(
  fixedProduct: FixedProductInput,
  productIndex: number,
  getValues: UseFormGetValues<PlanInputForm>,
): FieldPathMetadata<FixedProductInput>[] {
  if (
    isOneTimeProduct(fixedProduct) ||
    isSubscriptionProduct(fixedProduct) ||
    isCommissionProduct(fixedProduct) ||
    isRefundProduct(fixedProduct)
  ) {
    const productsPathsWithLabels: FieldPathMetadata<FixedProductInput>[] = [
      {
        path: 'variants.0.description',
        label: 'Description',
        tooltip: {
          title: withDefault(getValues(`fixedProducts.${productIndex}.variants.0.description`), ''),
        },
      },
    ];

    const discountConfiguration = getDiscountConfiguration(fixedProduct);

    if (
      isNotNullOrUndefined(discountConfiguration?.amountToDeduct) ||
      isNotNullOrUndefined(discountConfiguration?.name) ||
      isNotNullOrUndefined(discountConfiguration?.type)
    ) {
      productsPathsWithLabels.push({
        path: 'variants.0.pricing.priceConfiguration.discountConfiguration.name',
        label: 'Discount',
      });
    }

    if (isCreditProduct(fixedProduct)) {
      if (fixedProduct.variants[0].settings?.productConfiguration?.shouldCreateSeparateInvoice) {
        productsPathsWithLabels.push({
          path: 'variants.0.settings.productConfiguration.shouldCreateSeparateInvoice',
          label: 'Separate Invoice',
        });
      }

      if (isNotNullOrUndefined(fixedProduct.paymentTerm)) {
        productsPathsWithLabels.push({
          path: 'paymentTerm',
          label: fixedProduct.paymentTerm === PAYMENT_TERMS.Prepayment ? 'Prepaid' : 'Postpaid',
        });
      }
    }

    const variant = fixedProduct.variants.at(0);

    if (isNotNullOrUndefined(variant) && isSubscriptionProductVariant(variant)) {
      if (isNotNullOrUndefined(variant.pricing.priceConfiguration?.seats)) {
        productsPathsWithLabels.push({
          path: 'variants.0.pricing.priceConfiguration.seats',
          label: 'Per Unit Price',
        });
      }
    }

    return productsPathsWithLabels;
  }

  if (isCommercialTermsProduct(fixedProduct)) {
    const commercialTermsPathsWithLabels: FieldPathMetadata<FixedProductInput>[] = [
      {
        path: 'variants.0.description',
        label: 'Description',
        tooltip: {
          title: withDefault(getValues(`fixedProducts.${productIndex}.variants.0.description`), ''),
        },
      },
    ];

    const firstVariant = fixedProduct.variants.at(0);

    if (isNullOrUndefined(firstVariant) || !isCommercialTermsProductVariant(firstVariant)) {
      return commercialTermsPathsWithLabels;
    }

    if (firstVariant.pricing.priceConfiguration?.shouldCreateSeparateInvoice) {
      commercialTermsPathsWithLabels.push({
        path: 'variants.0.pricing.priceConfiguration.shouldCreateSeparateInvoice',
        label: 'Separate Invoice',
      });
    }

    const scheduledInstallments = firstVariant.pricing.priceConfiguration?.scheduledInstallments;

    if (isMultiple(scheduledInstallments?.installments)) {
      commercialTermsPathsWithLabels.push({
        path: 'variants.0.pricing.priceConfiguration.scheduledInstallments.installments',
        label: 'Installments',
      });
    }

    const installments = firstVariant.pricing.priceConfiguration?.installments ?? 1;

    if (!isCommercialTermsWithoutScheduledInstallments(fixedProduct) && installments > 1) {
      commercialTermsPathsWithLabels.push({
        path: 'variants.0.pricing.priceConfiguration.installments',
        label: 'Installments',
      });
    }

    if (isNotNullOrUndefined(fixedProduct.paymentTerm)) {
      commercialTermsPathsWithLabels.push({
        path: 'paymentTerm',
        label: fixedProduct.paymentTerm === PAYMENT_TERMS.Prepayment ? 'Prepaid' : 'Postpaid',
      });
    }

    const discountConfiguration = getDiscountConfiguration(fixedProduct);

    if (
      isNotNullOrUndefined(discountConfiguration?.amountToDeduct) ||
      isNotNullOrUndefined(discountConfiguration?.name) ||
      isNotNullOrUndefined(discountConfiguration?.type)
    ) {
      commercialTermsPathsWithLabels.push({
        path: 'variants.0.pricing.priceConfiguration.discountConfiguration.name',
        label: 'Discount',
      });
    }

    return commercialTermsPathsWithLabels;
  }

  if (isFixedDeductionProductOnly(fixedProduct) || isPercentageDeductionProductOnly(fixedProduct)) {
    return [
      {
        path: 'variants.0.description',
        label: 'Description',
        tooltip: {
          title: withDefault(getValues(`fixedProducts.${productIndex}.variants.0.description`), ''),
        },
      },
    ];
  }

  return raise('Invalid product type');
}
